import { markRaw } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import {
  JobCreatedAtColumnFragment,
  JobCreatedAtColumnFragmentDoc,
  JobHireAndPoNumberColumnFragment,
  JobHireAndPoNumberColumnFragmentDoc,
  JobSharedChannelsColumnFragment,
  JobSharedChannelsColumnFragmentDoc,
  JobStartDateColumnFragment,
  JobStartDateColumnFragmentDoc,
  JobEndDateColumnFragment,
  JobEndDateColumnFragmentDoc,
  JobCandidatesBidsHiresColumnFragment,
  JobCandidatesBidsHiresColumnFragmentDoc,
  JobOwnersColumnFragment,
  JobOwnersColumnFragmentDoc,
  RateColumnJobFragmentDoc,
  RateColumnJobFragment,
  SortOrder,
  JobOrderByColumn,
} from '@/gql/graphql'
import JobPoNumberColumn from '@/components/GraphqlDataTable/columns/Job/JobHirePoNumberColumn.vue'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'
import CreatedAtColumn from '@/components/GraphqlDataTable/columns/CreatedAtColumn.vue'
import JobSharedChannelsColumn from '@/components/GraphqlDataTable/columns/Job/JobSharedChannelsColumn.vue'
import JobStartDateColumn from '@/components/GraphqlDataTable/columns/Job/JobStartDateColumn.vue'
import JobEndDateColumn from '@/components/GraphqlDataTable/columns/Job/JobEndDateColumn.vue'
import JobCandidatesBidsHiresColumn from '@/components/GraphqlDataTable/columns/Job/JobCandidatesBidsHiresColumn.vue'
import JobOwnersColumn from '@/components/GraphqlDataTable/columns/Job/JobOwnersColumn.vue'
import RateColumn from '@/components/GraphqlDataTable/columns/RateColumn.vue'

const columns: ColumnMaps = {
  JobHireAndPoNumberColumn: {
    fragment: JobHireAndPoNumberColumnFragmentDoc,
    component: markRaw(JobPoNumberColumn),
    columnDef: {
      title: $t('Job title'),
      key: 'job_title_url',
      renderUsing: {
        component: markRaw(JobPoNumberColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true, // We have a custom click event so we want that to fire instead of row select
    },
    dataDescriber: [
      {
        title: 'Job title',
        extraction: (row: JobHireAndPoNumberColumnFragment) => row.name,
      },
      {
        title: 'Job url',
        extraction: (row: JobHireAndPoNumberColumnFragment) =>
          `${window.location.origin}${laravelNamedRoute(namedRoutes.jobs.show, { job: row.id })}`,
      },
    ],
  },
  JobCreatedAtColumn: {
    fragment: JobCreatedAtColumnFragmentDoc,
    component: markRaw(CreatedAtColumn),
    columnDef: {
      title: $t('Created'),
      key: 'job_created_at',
      orderAble: true,
      renderUsing: {
        component: markRaw(CreatedAtColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Created at',
        extraction: (row: JobCreatedAtColumnFragment) => row.createdAt,
      },
    ],
    sort: [
      {
        name: `${JobOrderByColumn.CreatedAt}_${SortOrder.Desc}`,
        orderByColumn: JobOrderByColumn.CreatedAt,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Created at'),
        secondaryText: $t('New to old'),
        title: $t('Order by the create at date'),
      },
      {
        name: `${JobOrderByColumn.CreatedAt}_${SortOrder.Asc}`,
        orderByColumn: JobOrderByColumn.CreatedAt,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Created at'),
        secondaryText: $t('Old to new'),
        title: $t('Order by the create at date'),
      },
    ],
  },
  JobStartDateColumn: {
    fragment: JobStartDateColumnFragmentDoc,
    component: markRaw(JobStartDateColumn),
    columnDef: {
      title: $t('Expected Start Date'),
      orderAble: true,
      key: 'start_date',
      renderUsing: {
        component: markRaw(JobStartDateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Start date',
        extraction: (row: JobStartDateColumnFragment) => row.startDate,
      },
      {
        title: 'Start date timeframe',
        extraction: (row: JobStartDateColumnFragment) => row.startDateTimeframe,
      },
    ],
    sort: [
      {
        name: `${JobOrderByColumn.ExpectedStartDate}_${SortOrder.Desc}`,
        orderByColumn: JobOrderByColumn.ExpectedStartDate,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Start date'),
        secondaryText: $t('New to old'),
        title: $t('Order by the start date'),
      },
      {
        name: `${JobOrderByColumn.ExpectedStartDate}_${SortOrder.Asc}`,
        orderByColumn: JobOrderByColumn.ExpectedStartDate,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Start date'),
        secondaryText: $t('Old to new'),
        title: $t('Order by the start date'),
      },
    ],
  },
  JobEndDateColumn: {
    fragment: JobEndDateColumnFragmentDoc,
    component: markRaw(JobEndDateColumn),
    columnDef: {
      title: $t('Duration'),
      key: 'end_date',
      renderUsing: {
        component: markRaw(JobEndDateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'End date',
        extraction: (row: JobEndDateColumnFragment) => row.endDate,
      },
      {
        title: 'End date timeframe',
        extraction: (row: JobEndDateColumnFragment) => row.endDateTimeframe,
      },
    ],
  },
  JobSharedChannelsColumn: {
    fragment: JobSharedChannelsColumnFragmentDoc,
    component: markRaw(JobSharedChannelsColumn),
    columnDef: {
      title: $t(''),
      key: 'publishedAt',
      renderUsing: {
        component: markRaw(JobSharedChannelsColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Published at',
        extraction: (row: JobSharedChannelsColumnFragment) => row.publishedAt ?? '',
      },
      {
        title: 'Published for marketplace',
        extraction: (row: JobSharedChannelsColumnFragment) => row.isForMarketplace,
      },
      {
        title: 'Published for vendors',
        extraction: (row: JobSharedChannelsColumnFragment) => row.isForRecruiters,
      },
      {
        title: 'Published for trusted contacts',
        extraction: (row: JobSharedChannelsColumnFragment) => row.isForContacts,
      },
    ],
    sort: [
      {
        name: `${JobOrderByColumn.PublishedDate}_${SortOrder.Desc}`,
        orderByColumn: JobOrderByColumn.PublishedDate,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Published date'),
        secondaryText: $t('New to old'),
        title: $t('Order by the start date'),
      },
      {
        name: `${JobOrderByColumn.PublishedDate}_${SortOrder.Asc}`,
        orderByColumn: JobOrderByColumn.PublishedDate,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Published date'),
        secondaryText: $t('Old to new'),
        title: $t('Order by the start date'),
      },
    ],
  },
  JobCandidatesBidsHiresColumn: {
    fragment: JobCandidatesBidsHiresColumnFragmentDoc,
    component: markRaw(JobCandidatesBidsHiresColumn),
    columnDef: {
      title: $t('Candidates | Hires'),
      orderAble: true,
      key: 'candidates',
      renderUsing: {
        component: markRaw(JobCandidatesBidsHiresColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Number of candidates',
        extraction: (row: JobCandidatesBidsHiresColumnFragment) =>
          `${row.workerCandidates.paginatorInfo.total}`,
      },
      {
        title: 'Number of hires',
        extraction: (row: JobCandidatesBidsHiresColumnFragment) =>
          `${row.hires.paginatorInfo.total}`,
      },
      {
        title: 'Number of required workers',
        extraction: (row: JobCandidatesBidsHiresColumnFragment) => `${row.requiredWorkers ?? '0'}`,
      },
    ],
    sort: [
      {
        name: `${JobOrderByColumn.Candidates}_${SortOrder.Desc}`,
        orderByColumn: JobOrderByColumn.Candidates,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Number of candidates'),
        secondaryText: $t('More to less'),
        title: $t('Order by number of candidates'),
      },
      {
        name: `${JobOrderByColumn.Candidates}_${SortOrder.Asc}`,
        orderByColumn: JobOrderByColumn.Candidates,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Number of candidates'),
        secondaryText: $t('Less to more'),
        title: $t('Order by number of candidates'),
      },
    ],
  },
  JobOwnersColumn: {
    fragment: JobOwnersColumnFragmentDoc,
    component: markRaw(JobOwnersColumn),
    columnDef: {
      title: $t('Job owner(s)'),
      key: 'job_owners',
      renderUsing: {
        component: markRaw(JobOwnersColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Job owner(s)',
        extraction: (row: JobOwnersColumnFragment) =>
          row.owners.reduce((acc, owner) => `${acc} ${owner.name} (${owner.id})`, ''),
      },
    ],
  },
  RateColumnJob: {
    fragment: RateColumnJobFragmentDoc,
    component: markRaw(RateColumn),
    columnDef: {
      title: $t('Rate'),
      key: 'rate',
      flexClass: { 'tw-float-right': true },
      renderUsing: {
        component: markRaw(RateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Rate',
        extraction: (row: RateColumnJobFragment) => `${row.rate}`,
      },
      {
        title: 'Rate type',
        extraction: (row: RateColumnJobFragment) => `${row.rateType}`,
      },
      {
        title: 'Currency',
        extraction: (row: RateColumnJobFragment) => `${row.currency}`,
      },
    ],
  },
}
export default columns
